export const muiTooltipBackgroundColor = '#616161E6';

//https://www.figma.com/design/i5EMdVKJDJhxoDN8cZ8K0x/WEB?node-id=13720-216&t=3IljOL4Nv7s0Hh2m-0
export const enum Color {
  white = '#ffffff',
  /** Hairline on dark surfaces (black CTA menus, split button dividers). */
  whiteBorder24 = 'rgba(255, 255, 255, 0.24)',
  /** Semi-transparent white overlay for readonly/disabled surfaces. */
  whiteOverlay60 = 'rgba(255, 255, 255, 0.6)',
  gray = '#FFFFFF66',
  solidGray = '#F2F2F2',
  iconGray = '#979797',
  /** Material Grey 600 — muted pill fill on dark surfaces (e.g. "no current version" in the gallery). */
  grey600 = '#757575',
  lightGray = '#BFBFBF',
  slateGray = '#525659',
  disabledGray = '#e0e0e0',
  /** Light neutral grey (#EEEEEE) — subtle surfaces and light-on-dark icons. */
  grayEee = '#EEEEEE',
  blackWithOpacity = '#0000000A',

  purpleWithOpacity = '#8E24AA80',
  purple = '#8E24AA',
  /** Subtle purple tint (e.g. selection background) — pairs with `Color.purple` */
  purpleLight = 'rgba(142, 36, 170, 0.1)',

  red = '#E53935',
  redWithOpacity = '#E5393510',
  /** Material Green 600 — compliance toggles, success affordances alongside `statusSuccessGreen`. */
  accentGreen600 = '#43A047',
  accentGreen600Bg10 = 'rgba(67, 160, 71, 0.1)',
  accentGreen600Bg15 = 'rgba(67, 160, 71, 0.15)',
  accentGreen600Bg25 = 'rgba(67, 160, 71, 0.25)',
  redBg13 = 'rgba(229, 57, 53, 0.13)',
  redBg18 = 'rgba(229, 57, 53, 0.18)',
  redBg28 = 'rgba(229, 57, 53, 0.28)',
  /** Neutral muted text and borders (templates, toggle groups). */
  neutralBorderGray = '#8A8A8A',

  blue = '#315FA3',
  blueWithOpacity = '#315FA335',
  /** Subtle blue surface for contextual info bars — pairs with `Color.blue`. */
  blueBg08 = 'rgba(49, 95, 163, 0.08)',
  /** Hairline border for blue contextual surfaces — pairs with `Color.blueBg08`. */
  blueBorder18 = 'rgba(49, 95, 163, 0.18)',

  black = '#000000',
  blackMuted = '#000000CC',
  /** Hover fill for the solid black primary CTA (page-level create buttons). */
  ctaBlackHover = '#262626',

  primaryOrange = '#FBCC30',
  /** Soft cream tint for the app header (menubar) — pairs with the brand-yellow keyline. */
  menuBarTint = '#FAF1CF',
  /** Warm cream tint for the selected table row — pairs with the menubar tint. */
  selectedRowTint = '#FBF4D8',
  primaryLightOrange = '#E4BA2C',
  /**
   * Darkened primary for primary-colored TEXT on light surfaces (e.g. focused
   * input labels). `primaryOrange`/`primaryLightOrange` are too light for text
   * (~1.5:1 / ~1.8:1 on white); this tone reaches ~4.9:1, passing WCAG 1.4.3.
   * Keep the bright `primaryOrange` for fills/borders, not legible text.
   */
  primaryOrangeDark = '#8A6D00',
  /** Pale primary tint — selected state in segmented controls, subtle highlights. */
  primaryOrangePale = '#FFEEA0',
  /** Mid primary tint between `primaryOrangePale` and `primaryOrange` (inclusive yellow 100). */
  primaryOrangeSoft = '#fddc69',
  primaryOrangeWithOpacity = '#FBCC3073',
  primaryOrangeLight = 'rgba(251, 204, 48, 0.08)',
  /** Visible primary-tinted hover (rows, text/icon buttons) — stronger than the default ~4% neutral hover. */
  primaryOrangeHover = 'rgba(251, 204, 48, 0.16)',
  /**
   * Hover fill for solid primary (contained) buttons. A slightly deeper but
   * still vivid yellow — MUI's default `primary.dark` darkening turns the
   * brand yellow into a muddy olive, which reads poorly.
   */
  primaryOrangeHoverSolid = '#EFC01F',
  /**
   * Deeper hover fill for solid primary (contained) buttons — a rich, warm
   * gold that is clearly darker than the brand yellow while staying vivid
   * (not the muddy olive of MUI's default `primary.dark`).
   */
  primaryOrangeHoverDeep = '#E0A800',
  /** Pressed/active primary-tinted state, pairs with `primaryOrangeHover`. */
  primaryOrangeActive = 'rgba(251, 204, 48, 0.3)',

  // MUI palette aliases (success / error / warning / disabled text)
  successGreen = '#2e7d32',
  successGreenDark = '#1b5e20',
  /** Positive status accent (inspection completed, compliance passed, published template). */
  statusSuccessGreen = '#00904C',
  /** In-progress / draft status (inspection active, template draft). */
  statusDraftAmber = '#FF9800',
  /**
   * Legible text tone for the in-progress/draft amber badge on a pale-yellow
   * fill (pairs with `primaryOrangePale`). Bright `statusDraftAmber` and
   * `primaryOrangeDark` only reach ~4.2:1 there; this darker gold passes
   * WCAG 1.4.3 (~6:1).
   */
  statusDraftAmberText = '#6B5400',
  /** Subtle amber surface for option-selection answer accents. */
  statusDraftAmberBg08 = 'rgba(255, 152, 0, 0.08)',
  errorRed = '#c62828',
  errorRedDark = '#b71c1c',
  warningOrange = '#ed6c02',
  mutedBlack = 'rgba(0,0,0,0.38)',

  // Text emphasis levels (semi-transparent black)
  textActive = 'rgba(0,0,0,0.87)',
  textSecondary = 'rgba(0,0,0,0.78)',
  textPrimary = 'rgba(0,0,0,0.72)',
  textIcon = 'rgba(0,0,0,0.6)',
  textMuted = 'rgba(0,0,0,0.55)',
  textSubtle = 'rgba(0,0,0,0.45)',
  border = 'rgba(0,0,0,0.12)',
  hoverBackground = 'rgba(0,0,0,0.14)',

  /** Black scrims — workflow stepper, mobile chrome. */
  scrimBlack06 = 'rgba(0,0,0,0.06)',
  scrimBlack07 = 'rgba(0,0,0,0.07)',
  shadowBlack016 = 'rgba(0,0,0,0.16)',
  shadowBlack018 = 'rgba(0,0,0,0.18)',
  /** Ring around current step dot (blends with solidGray sidebar). */
  solidGrayRing90 = 'rgba(242,242,242,0.9)',
  floatingWorkflowSurface = 'rgba(242, 242, 242, 0.97)',
  floatingWorkflowSurfaceHover = 'rgba(230, 230, 230, 0.99)',
  /** MUI-style success / error fills (pair with `successGreen` / `errorRed`). */
  successGreenBg08 = 'rgba(46, 125, 50, 0.08)',
  successGreenBg14 = 'rgba(46, 125, 50, 0.14)',
  errorRedBg08 = 'rgba(198, 40, 40, 0.08)',
  errorRedBg14 = 'rgba(198, 40, 40, 0.14)',
}

const FALLBACK_RGB: [number, number, number] = [0, 0, 0];

/**
 * Parse a hex color string into RGB components.
 * Supports formats: "#RGB", "#RRGGBB", "#RRGGBBAA", "RGB", "RRGGBB", "RRGGBBAA"
 * @param hex - Color in hex format
 * @returns Tuple of [r, g, b] values (0-255), or [0, 0, 0] if format is invalid
 */
export function parseHexColor(hex: string): [number, number, number] {
  if (!hex || typeof hex !== 'string') {
    return FALLBACK_RGB;
  }

  let h = hex.replace('#', '');

  // Expand short format #RGB to #RRGGBB
  if (h.length === 3) {
    h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  }

  // Handle #RRGGBBAA by stripping alpha
  if (h.length === 8) {
    h = h.substring(0, 6);
  }

  if (h.length !== 6 || !/^[0-9A-Fa-f]{6}$/.test(h)) {
    return FALLBACK_RGB;
  }

  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}

/**
 * Interpolate between two hex colors.
 * @param color1 - Start color in hex format
 * @param color2 - End color in hex format
 * @param ratio - Interpolation ratio (0 = color1, 1 = color2)
 * @returns Interpolated color in uppercase hex format
 */
export function interpolateHexColor(
  color1: string,
  color2: string,
  ratio: number
): string {
  const rgb1 = parseHexColor(color1);
  const rgb2 = parseHexColor(color2);
  const interpolated = rgb1.map((c, i) => Math.round(c + (rgb2[i] - c) * ratio));
  return `#${interpolated.map((n) => n.toString(16).padStart(2, '0')).join('')}`.toUpperCase();
}
